import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import React, { useEffect } from 'react'
import {
  getJobProgressSummary,
  getProcessResultItems,
  getStatusItems,
} from './formatUtils'
import { getProcessResults, startProcess } from './utils'
import {
  pipelineErrorAtom,
  prepareResultAtom,
  prepareStatusAtom,
  processResultAtom,
  processStatusAtom,
  selectedModelAtom,
  selectedProviderAtom,
} from './atoms'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'

import { FlexBoxHelper } from './FlexBoxHelper'
import { JobProgressBar } from './JobProgressBar'

export function ProcessStage({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const status = useAtomValue(processStatusAtom)
  const prepareStatus = useAtomValue(prepareStatusAtom)
  const selectedProvider = useAtomValue(selectedProviderAtom)
  const selectedModel = useAtomValue(selectedModelAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const prepareResult = useAtomValue(prepareResultAtom)
  const processResult = useAtomValue(processResultAtom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setProcessResult = useSetAtom(processResultAtom)
  const setSelectedProvider = useSetAtom(selectedProviderAtom)
  const setSelectedModel = useSetAtom(selectedModelAtom)

  // Reset model when provider changes
  useEffect(() => {
    if (selectedProvider === 'openai' && selectedModel === 'gemini-pro') {
      setSelectedModel('gpt-4')
    } else if (
      selectedProvider === 'gemini' &&
      (selectedModel === 'gpt-4' || selectedModel === 'gpt-5')
    ) {
      setSelectedModel('gemini-pro')
    }
  }, [selectedProvider, selectedModel, setSelectedModel])

  // Helper function to check if a model should be enabled
  const isModelEnabled = (modelValue: string) => {
    if (!selectedProvider) return false
    if (selectedProvider === 'openai') {
      return modelValue === 'gpt-4' || modelValue === 'gpt-5'
    }
    if (selectedProvider === 'gemini') {
      return modelValue === 'gemini-pro'
    }
    return false
  }

  const handleStartStage = async () => {
    try {
      if (prepareStatus?.status !== 'success') {
        throw new Error('Prepare results not available')
      }

      if (!selectedProvider) {
        throw new Error('No provider selected')
      }

      await startProcess(
        treeId,
        versionId,
        prepareStatus.controller_id,
        selectedProvider,
        selectedModel,
      )
      // The PipelineStatus component will pick up the new job status via polling
    } catch (error) {
      setPipelineError(
        `Error starting process stage: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  // Fetch process results when status is completed, no error, and result is null
  useEffect(() => {
    const fetchResults = async () => {
      if (!pipelineError && status?.flow_status?.completed && !processResult) {
        try {
          const results = await getProcessResults(treeId, versionId)
          if (results.data?.results?.[0]) {
            setProcessResult(results.data.results[0])
          }
        } catch (error) {
          setPipelineError(
            `Error fetching process results: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        }
      }
    }

    fetchResults()
  }, [
    pipelineError,
    status?.flow_status?.completed,
    processResult,
    treeId,
    versionId,
    setProcessResult,
    setPipelineError,
  ])

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Process
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Provider</InputLabel>
          <Select
            value={selectedProvider}
            label="Provider"
            disabled={
              pipelineError !== null || prepareStatus?.status !== 'success'
            }
            onChange={(e) => setSelectedProvider(e.target.value)}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="gemini">Gemini</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Model</InputLabel>
          <Select
            value={selectedModel}
            label="Model"
            disabled={
              pipelineError !== null || prepareStatus?.status !== 'success'
            }
            onChange={(e) => setSelectedModel(e.target.value)}
          >
            <MenuItem value="gpt-4" disabled={!isModelEnabled('gpt-4')}>
              GPT-4
            </MenuItem>
            <MenuItem value="gpt-5" disabled={!isModelEnabled('gpt-5')}>
              GPT-5
            </MenuItem>
            <MenuItem
              value="gemini-2.5-flash"
              disabled={!isModelEnabled('gemini-pro')}
            >
              Gemini Pro
            </MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartStage}
          disabled={
            pipelineError !== null ||
            !selectedProvider ||
            prepareStatus?.status !== 'success'
          }
          size="small"
        >
          Start
        </Button>
      </Box>

      {status && (
        <FlexBoxHelper
          header={
            !status?.flow_status?.completed ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="primary" />
                <Typography variant="body2" color="primary">
                  {status.flow_status
                    ? getJobProgressSummary(status.flow_status)
                    : 'Processing...'}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: 'bold' }}
              >
                ✓ Completed Successfully
              </Typography>
            )
          }
          items={getStatusItems(status, null)}
        />
      )}

      {status?.flow_status && !status.flow_status.completed && (
        <JobProgressBar flowStatus={status.flow_status} />
      )}

      {processResult && (
        <FlexBoxHelper
          header={<Typography variant="h6">Process Results</Typography>}
          items={getProcessResultItems(processResult)}
        />
      )}
    </Box>
  )
}
